import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  useTheme,
  alpha,
  IconButton,
  Stack,
  Divider,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import TerminalIcon from "@mui/icons-material/Terminal";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import CodeIcon from "@mui/icons-material/Code";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";

export const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        py: { xs: 6, md: 8 },
        mt: 6, // Margem superior para separar do conteúdo
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5} sx={{ mb: 5 }}>
          {/* Coluna 1: Logo e Social */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <TerminalIcon
                sx={{ color: theme.palette.primary.light, fontSize: 28 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  letterSpacing: "0.05em",
                }}
              >
                PLANOS_DEV
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Tecnologia e inteligência para encontrar o plano de saúde ideal
              para desenvolvedores e profissionais de TI.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {[GitHubIcon, LinkedInIcon, TwitterIcon].map((Icon, index) => (
                <IconButton
                  key={index}
                  aria-label={Icon.name?.replace("Icon", "").toLowerCase()}
                  href="#" // Adicionar links reais aqui
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.light,
                      borderColor: theme.palette.primary.light,
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Coluna 2: Navegação */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                display: "block",
              }}
            >
              Navegação
            </Typography>
            <Stack spacing={1.5}>
              {["Cobertura", "Planos", "Tecnologia", "Contato"].map((item) => (
                <Link
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  underline="hover"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.9rem",
                    "&:hover": { color: theme.palette.primary.light },
                  }}
                >
                  {item}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Coluna 3: Recursos */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                display: "block",
              }}
            >
              Recursos
            </Typography>
            <Stack spacing={1.5}>
              {["Documentação", "API Status", "Blog Técnico", "FAQ Dev"].map(
                (item) => (
                  <Link
                    key={item}
                    href="#"
                    underline="hover"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.9rem",
                      "&:hover": { color: theme.palette.primary.light },
                    }}
                  >
                    {item}
                  </Link>
                )
              )}
            </Stack>
          </Grid>

          {/* Coluna 4: Newsletter/Contato Rápido */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                display: "block",
              }}
            >
              Fique Atualizado
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Receba novidades sobre planos e tecnologia no seu e-mail.
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="seu_email@provedor.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon
                      sx={{ color: theme.palette.text.secondary }}
                      fontSize="small"
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      sx={{ color: theme.palette.primary.light }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center" }}
            >
              Sem spam, apenas conteúdo relevante.
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <Box
          sx={{
            py: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            © {currentYear} PLANOS_DEV | Todos os direitos reservados.
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            <CodeIcon
              sx={{ fontSize: "inherit", verticalAlign: "middle", mr: 0.5 }}
            />
            Desenvolvido por{" "}
            <Link
              href="#"
              color="inherit"
              sx={{ color: theme.palette.primary.light }}
            >
              Devs for Devs
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
